/**
 * The composer's dictation, as a small machine: what the row shows, and what
 * the hook must do next. Pure, so `bun test` proves every turn of it;
 * `use-dictation.ts` holds the recorder, the socket and the timers, and runs
 * the effects this hands back.
 *
 * Two paths. On a server with a transcriber the words stream back while the
 * person speaks (`live`, `dictation-socket.ts`); on one without, or when the
 * socket does not open in time, the recording is uploaded when it stops
 * (`upload`, `/v1/agent/transcribe`). The recording is kept on both, so a
 * slow open falls back to the upload and loses nothing.
 *
 *   recording     the mic is open; on the live path, `heard` is the words so far
 *   settling      the check, a tap on the words or the cap: the socket is
 *                 settling the last words (live)
 *   transcribing  the recording is being uploaded (upload)
 *   held          the cap: the recording stopped, and its words wait for the
 *                 check or a tap
 *   nothing_heard the words came back empty
 *   failed        the recorder, the socket or the upload failed; words heard
 *                 before a drop are handed on to be kept, never sent
 *   mic_off       the microphone was refused
 *
 * `hand` is the one effect that reaches the composer: the words, and whether
 * to send them (the check), put them in the box to edit (a tap on the words),
 * or keep them there after a drop.
 */

export type DictationStatus = 'idle' | 'recording' | 'settling' | 'transcribing' | 'held' | 'nothing_heard' | 'failed' | 'mic_off';

export type DictationPath = 'live' | 'upload';

/** What the composer does with words handed to it: send them, put them in the box to edit, or keep them there after a drop. */
export type DictationIntent = 'send' | 'edit' | 'keep';

/** What the person asked for when the recording stopped: the check, a tap on the words, or the cap. */
export type DictationAfter = 'send' | 'edit' | 'hold';

export type DictationState = {
  readonly status: DictationStatus;
  readonly path: DictationPath;
  /** The words so far on the live path; the held words at the cap. */
  readonly heard: string;
  /** The server said `open`: the transcriber is hearing. */
  readonly opened: boolean;
  readonly after: DictationAfter | null;
};

export type DictationEvent =
  | { readonly type: 'start'; readonly path: DictationPath }
  | { readonly type: 'mic_refused' }
  | { readonly type: 'recorder_failed' }
  | { readonly type: 'open' }
  | { readonly type: 'words'; readonly text: string }
  /** The socket failed, closed when nobody asked it to, or did not open in time. */
  | { readonly type: 'lost' }
  /** The check. */
  | { readonly type: 'send' }
  /** A tap on the words. */
  | { readonly type: 'edit' }
  /** Sixty seconds of recording. */
  | { readonly type: 'cap' }
  /** The words are final: the socket's `done`, the settle backstop, or the upload's answer. */
  | { readonly type: 'settled'; readonly text: string }
  /** The upload had nothing to hear. */
  | { readonly type: 'nothing' }
  | { readonly type: 'upload_failed' }
  /** X. */
  | { readonly type: 'cancel' };

export type DictationEffect =
  | { readonly type: 'stop_recorder' }
  | { readonly type: 'finish_socket' }
  | { readonly type: 'close_socket' }
  /** X: the socket is told to drop everything (`cancel`), then closed. */
  | { readonly type: 'cancel_socket' }
  | { readonly type: 'upload' }
  | { readonly type: 'settle_timer' }
  | { readonly type: 'hand'; readonly text: string; readonly intent: DictationIntent };

export type DictationStep = { readonly state: DictationState; readonly effects: readonly DictationEffect[] };

export const DICTATION_IDLE: DictationState = { status: 'idle', path: 'live', heard: '', opened: false, after: null };

/**
 * A dictation is under way: from the tap on the mic until its words are
 * handed on or dropped, the held words at the cap included. The row stands in
 * the composer's place, and nothing may carry the conversation away under it.
 */
export const dictationUnderWay = (status: DictationStatus): boolean =>
  status === 'recording' || status === 'settling' || status === 'transcribing' || status === 'held';

/**
 * Whether the conversation's header may open New or History. Both wait while
 * a turn is in flight, and while a dictation is under way: its words belong
 * to this conversation, so checked words never land in the next one, and the
 * mic never records under the list. `status` is null on a panel with no
 * composer.
 */
export const browseOpen = (busy: boolean, status: DictationStatus | null): boolean =>
  !busy && (status === null || !dictationUnderWay(status));

/**
 * What a refused upload means, by the server's code: `VOICE_NO_SPEECH` is a
 * recording with no speech in it (`agent/voice.ts`), so nothing was heard;
 * anything else — a provider refusing the upload is a 422 too — is a failed
 * upload, never blamed on the room.
 */
export const uploadRefusal = (code: string | null): DictationEvent => (code === 'VOICE_NO_SPEECH' ? { type: 'nothing' } : { type: 'upload_failed' });

const stay = (state: DictationState): DictationStep => ({ state, effects: [] });

/** The words heard before a drop, to keep in the box: nothing when nothing was heard. */
const keep = (heard: string): readonly DictationEffect[] => (heard.trim() === '' ? [] : [{ type: 'hand', text: heard, intent: 'keep' }]);

const afterOf = (event: 'send' | 'edit' | 'cap'): DictationAfter => (event === 'cap' ? 'hold' : event);

export const dictationStep = (state: DictationState, event: DictationEvent): DictationStep => {
  const { status } = state;
  const live = state.path === 'live';

  if (event.type === 'cancel') {
    if (status === 'idle') return stay(state);
    return { state: { ...state, status: 'idle', heard: '', opened: false, after: null }, effects: [{ type: 'stop_recorder' }, { type: 'cancel_socket' }] };
  }

  switch (status) {
    case 'idle':
    case 'nothing_heard':
    case 'failed':
    case 'mic_off':
      if (event.type === 'start') return { state: { status: 'recording', path: event.path, heard: '', opened: false, after: null }, effects: [] };
      return stay(state);

    case 'recording':
      switch (event.type) {
        case 'mic_refused':
          return { state: { ...state, status: 'mic_off' }, effects: [{ type: 'close_socket' }] };
        case 'recorder_failed':
          return { state: { ...state, status: 'failed' }, effects: [{ type: 'stop_recorder' }, { type: 'close_socket' }] };
        case 'open':
          return live ? { state: { ...state, opened: true }, effects: [] } : stay(state);
        case 'words':
          return live ? { state: { ...state, heard: event.text }, effects: [] } : stay(state);
        case 'lost':
          if (!live) return stay(state);
          // The transcriber never came: the recording goes on, and is uploaded (the bars appear).
          if (!state.opened) return { state: { ...state, path: 'upload' }, effects: [{ type: 'close_socket' }] };
          return {
            state: { ...state, status: 'failed', heard: '', after: null },
            effects: [{ type: 'stop_recorder' }, { type: 'close_socket' }, ...keep(state.heard)],
          };
        case 'send':
        case 'edit':
        case 'cap': {
          const after = afterOf(event.type);
          if (live && state.opened) {
            return { state: { ...state, status: 'settling', after }, effects: [{ type: 'stop_recorder' }, { type: 'finish_socket' }, { type: 'settle_timer' }] };
          }
          if (live) {
            // The check before the transcriber opened: what was recorded is uploaded, so nothing is lost to a slow open.
            return {
              state: { ...state, status: 'transcribing', path: 'upload', after },
              effects: [{ type: 'stop_recorder' }, { type: 'close_socket' }, { type: 'upload' }],
            };
          }
          // No words to tap on the upload path.
          if (event.type === 'edit') return stay(state);
          return { state: { ...state, status: 'transcribing', after }, effects: [{ type: 'stop_recorder' }, { type: 'upload' }] };
        }
        default:
          return stay(state);
      }

    case 'settling':
    case 'transcribing':
      if (event.type === 'words' && status === 'settling' && live) return { state: { ...state, heard: event.text }, effects: [] };
      if (event.type === 'lost' && status === 'settling') {
        return { state: { ...state, status: 'failed', heard: '', after: null }, effects: [{ type: 'close_socket' }, ...keep(state.heard)] };
      }
      if (event.type === 'settled') {
        const text = event.text.trim();
        if (text === '') return { state: { ...state, status: 'nothing_heard', heard: '', after: null }, effects: [{ type: 'close_socket' }] };
        if (state.after === 'hold') return { state: { ...state, status: 'held', heard: text }, effects: [{ type: 'close_socket' }] };
        return {
          state: { ...state, status: 'idle', heard: '', after: null },
          effects: [{ type: 'close_socket' }, { type: 'hand', text, intent: state.after === 'edit' ? 'edit' : 'send' }],
        };
      }
      if (event.type === 'nothing' && status === 'transcribing') return { state: { ...state, status: 'nothing_heard', heard: '', after: null }, effects: [] };
      if (event.type === 'upload_failed' && status === 'transcribing') return { state: { ...state, status: 'failed', heard: '', after: null }, effects: [] };
      // A second tap on the check, or on the words, does nothing.
      return stay(state);

    case 'held':
      if (event.type === 'send' || event.type === 'edit') {
        return { state: { ...state, status: 'idle', heard: '', after: null }, effects: [{ type: 'hand', text: state.heard, intent: event.type }] };
      }
      return stay(state);

    default:
      return stay(state);
  }
};
